import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Container, Paper, TextField, Button, Typography, Alert, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import Header from '../../components/Header/Header';
import { loginAppUser, setStoredUser, getStoredUser } from '../../lib/appAuth';
import './SignIn.css';

const SignIn = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (getStoredUser()) {
      navigate('/profile', { replace: true });
    }
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Basic validation
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const user = await loginAppUser(formData.email.trim(), formData.password);
      setStoredUser(user);

      navigate('/');
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.message || 'Sign in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  const handleNavigate = (page) => {
    const routes = {
      'home': '/',
      'listing': '/hotels',
      'about': '/about',
      'contact': '/contact',
      'test-g2': '/test-g2'
    };
    navigate(routes[page] || '/');
    window.scrollTo(0, 0);
  };

  return (
    <>
      <Header onLogoClick={handleGoHome} onNavigate={handleNavigate} />
      <Box className="signin-page">
        <Container maxWidth="sm">
          <Box className="signin-container">
            <Paper elevation={3} className="signin-paper">
              <Typography variant="h4" className="signin-title">
                Sign in to your account
              </Typography>
              <Typography variant="body2" className="signin-subtitle">
                Welcome back! Please enter your details.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="signin-form">
                <TextField
                  fullWidth
                  label="Email address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="signin-input"
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="signin-input"
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box className="signin-options">
                  <Box className="signin-remember">
                    <input type="checkbox" id="remember" />
                    <label htmlFor="remember">Remember me</label>
                  </Box>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  className="signin-submit-btn"
                  disabled={loading}
                  sx={{ mt: 2, mb: 2, py: 1.5 }}
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </Button>

                <Box className="signin-register-link">
                  <Typography variant="body2">
                    Don't have an account?{' '}
                    <Link to="/register" className="signin-link">
                      Create one here
                    </Link>
                  </Typography>
                </Box>
              </form>
            </Paper>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default SignIn;
